import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FolderTabInfos } from 'src/app/models/folder-tab-infos';
import { FolderTabEventsService } from 'src/app/services/events/folder-tab-events.service';
import { FoldersService } from 'src/app/services/http/folders.service';
import { NavigationStateService } from 'src/app/services/state/navigate-state.service';
import { OpenedFoldersStateService } from 'src/app/services/state/opened-folders-state.service';
import { ViewsStateService } from 'src/app/services/state/views-state.service';

import { CdkDropListGroup } from '@angular/cdk/drag-drop';
import { MatTabsModule } from '@angular/material/tabs';
import { TabSelectionType } from 'src/app/models/enums/tab-selection-type';
import { FolderCloseTabRequestEvent } from 'src/app/models/events/folder-close-tab-request-event';
import { ViewChangeEvent } from 'src/app/models/events/view-change-event';
import { Rights } from 'src/app/models/server/enums/rights';
import { ConnectedUsersStateService } from 'src/app/services/state/connected-users-state.service';
import { ProfileSettingsStateService } from 'src/app/services/state/profile-settings-state.service';
import { TabSelectedStateService } from 'src/app/services/state/tab-selected-state.service';
import { FolderDetailContainerComponent } from './folder-detail-container/folder-detail-container.component';
import { FoldersListComponent } from './folders-list/folders-list.component';
import { FollowedFoldersDatasListComponent } from './followed-folders-datas-list/followed-folders-datas-list.component';

@Component({
    templateUrl: './tabs-container.component.html',
    styleUrls: ['./tabs-container.component.scss'],
    imports: [CdkDropListGroup, MatTabsModule, FoldersListComponent, MatTooltipModule, FaIconComponent, FolderDetailContainerComponent, FollowedFoldersDatasListComponent]
})
export class TabsContainerComponent implements OnInit, OnDestroy {
    @ViewChild(FoldersListComponent) folderListComponent: FoldersListComponent;

    @ViewChild(FollowedFoldersDatasListComponent) followedFolderDatasListComponent: FollowedFoldersDatasListComponent;

    /**
     * Références des icônes utilisés dans le template.
     */
    public closeIcon = faXmark;

    /**
     * Utilisé pour gérer la désincription des observables lorsque le composant est détruit.
     */
    protected componentDestroyed$: Subject<void> = new Subject<void>();

    public tabs: FolderTabInfos[] = [];

    /**
     * Id du dossier correspondant à l'onglet sélectionné.
     */
    selectedTabIndex: number = 0;

    /**
     * Décalage des onglets affichant le détail des dossiers lié à la présence de l'onglet Liste en position 0.
     */
    private tabOffset: number = 1;

    public followFolderRight: boolean = false;

    public showfollowFolder: boolean = false;

    /**
     * Constructeur.
     * @param usersService
     * @param definitionService
     * @param navigateService
     * @param foldersService
     */
    constructor(
        private navigateService: NavigationStateService,
        private foldersService: FoldersService,
        private folderTabEventsService: FolderTabEventsService,
        private connectedUserStateService: ConnectedUsersStateService,
        private profileSettingsStateService: ProfileSettingsStateService,
        public openedFoldersStateService: OpenedFoldersStateService,
        public viewsStateService: ViewsStateService,
        public tabSelectedStateService: TabSelectedStateService
    ) {
        this.followFolderRight = connectedUserStateService.hasRight(Rights.FollowFolder);
        this.showfollowFolder = profileSettingsStateService.getProfileSettings().showFolderFollow;
    }

    /**
     * Appelée à l'initialisation du composant.
     */
    ngOnInit(): void {
        this.subscribeToNavigateEvent();
        this.subscribeToFolderTabEvent();
        this.subscribeToViewsStateEvent();
    }

    /**
     * Appelée à la destruction du composant.
     */
    ngOnDestroy(): void {
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }

    /**
     * Appelé lorsque l'utilisateur sélectionne un onglet.
     * @param tabIndex : Indice de l'onglet sélectionné.
     */
    public onTabChange(tabIndex: number) {
        this.selectedTabIndex = tabIndex;

        switch (tabIndex) {
            case 0:
                if (this.showfollowFolder) {
                    // Onglet 'Mes dossiers' sélectionné.
                    this.followedFolderDatasListComponent.restoreScrollPosition();
                    this.tabSelectedStateService.setSelectedTabType(TabSelectionType.MyFolders);
                } else {
                    // Onglet 'Vue' sélectionné.
                    this.folderListComponent.restoreScrollPosition();
                    this.tabSelectedStateService.setSelectedTabType(TabSelectionType.ViewFolders);
                }
                break;
            case 1:
                if (this.showfollowFolder) {
                    // Onglet 'Vue' sélectionné.
                    this.folderListComponent.restoreScrollPosition();
                    this.tabSelectedStateService.setSelectedTabType(TabSelectionType.ViewFolders);
                } else {
                    // Onglet 'Détail dossier' sélectionné.
                    this.tabSelectedStateService.setSelectedTabType(TabSelectionType.FolderDetail);
                }
                break;
            default:
                // Onglet 'Détail dossier' sélectionné.
                this.tabSelectedStateService.setSelectedTabType(TabSelectionType.FolderDetail);
                break;
        }
    }

    /**
     * Appelé lorsque l'utilisateur demande la fermeture de l'onglet d'un dossier.
     * @param tabIndex : Id du dossier affiché par l'onglet à fermer.
     */
    public onFolderTabClosed(folderId: string): void {
        this.removeTab(folderId);
    }

    /**
     * Appelé lorsque l'utilisateur demande l'affichage d'un dossier.
     * @param folder
     */
    public onFolderDisplayed(folder: FolderTabInfos): void {
        this.addTab(folder, true);
    }

    /**
     * S'abonne aux événements de navigation entre dossiers.
     */
    private subscribeToNavigateEvent() {
        this.navigateService.navigate$.pipe(takeUntil(this.componentDestroyed$)).subscribe((x) => {
            if (!!x) {
                if (x.currentFolder.id !== x.newFolder.id) {
                    // Si un autre onglet affiche déjà le dossier demandé, cet onglet est supprimé.
                    if (this.openedFoldersStateService.openedFolders.hasKey(x.newFolder.id)) {
                        this.removeTab(x.newFolder.id);
                    }

                    const originalFolderindex = this.tabs.findIndex((t) => t.folderId === x.originalFolder.id);

                    if (originalFolderindex !== -1) {
                        // Supprime l'onglet affichant le dossier precedent.
                        this.removeTab(x.originalFolder.id);

                        // Remplace l'onglet supprimé par un onglet avec le dossier demandé.
                        this.insertTab(originalFolderindex, new FolderTabInfos(x.newFolder.id, x.newFolder.name, x.view, x.readonly), true);
                    }
                }
            }
        });
    }

    /**
     * S'abonne aux événements de mise à jour de dossier.
     */
    private subscribeToFolderTabEvent() {
        this.folderTabEventsService.closed.pipe(takeUntil(this.componentDestroyed$)).subscribe((event: FolderCloseTabRequestEvent) => {
            if (this.openedFoldersStateService.openedFolders.hasKey(event.folderId)) {
                //console.warn(`Réception de l'événement FolderTabClosedEvent par le composant MainComponent pour le dossier ${event.folderId}`, event);
                this.removeTab(event.folderId, event.isFolderRemoved);
            }
        });

        this.folderTabEventsService.renamed.pipe(takeUntil(this.componentDestroyed$)).subscribe((event) => {
            if (this.openedFoldersStateService.openedFolders.hasKey(event.folderId)) {
                //console.warn(`Réception de l'événement FolderTabRenamedEvent par le composant MainComponent pour le dossier ${event.folderId}`, event);
                this.openedFoldersStateService.openedFolders.get(event.folderId).folderName = event.folderName;
            }
        });
    }

    /**
     * S'abonne aux évènements de changement de vue
     */
    private subscribeToViewsStateEvent() {
        this.viewsStateService.onViewsSelectionChange$.pipe(takeUntil(this.componentDestroyed$)).subscribe((viewChangeEvent: ViewChangeEvent) => {
            if (viewChangeEvent?.currentView && this.tabSelectedStateService.getSelectedTabType() === TabSelectionType.ViewFolders) {
                // Lorsque la vue selectionnée est modifiée par l'utilisateur, on emet un evenement de changement d'onglet pour rafraichier le texte de la 'Vue' sélectionnée dans le bandeau.
                this.tabSelectedStateService.setSelectedTabType(TabSelectionType.ViewFolders);
            }
        });
    }

    /**
     * Crée un onglet pour acceder au détail d'un dossier.
     * @param folderTabInfos Infos du dossier a afficher dans l'onglet.
     * @param selectAfterAdding Valeur indiquant si l'onglet doit être sélectionné après sa création.
     */
    private addTab(folderTabInfos: FolderTabInfos, selectAfterAdding: boolean): void {
        let tabIndex = this.tabs.findIndex((x) => x.folderId === folderTabInfos.folderId);

        if (this.openedFoldersStateService.openedFolders.hasKey(folderTabInfos.folderId)) {
            this.openedFoldersStateService.openedFolders.set(folderTabInfos.folderId, folderTabInfos); // Force les infos en cas de passage de lecture seule à edition ou vice-versa.

            if (folderTabInfos.readonly) {
                // Libère le lock sur le dossier.
                this.foldersService.unlock(folderTabInfos.view.id, folderTabInfos.folderId).pipe(takeUntil(this.componentDestroyed$)).subscribe();
            }
        } else {
            // Crée un nouvel onglet si le dossier n'est pas déjà ouvert dans un onglet existant.
            this.openedFoldersStateService.openedFolders.set(folderTabInfos.folderId, folderTabInfos);
            this.tabs.push(folderTabInfos);
            tabIndex =
                this.profileSettingsStateService.getProfileSettings().showFolderFollow && this.connectedUserStateService.hasRight(Rights.FollowFolder)
                    ? this.tabs.length
                    : this.tabs.length - 1;
        }

        // Sélectionne l'onglet, si cela est demandé.
        if (selectAfterAdding) {
            this.selectedTabIndex =
                this.profileSettingsStateService.getProfileSettings().showFolderFollow && this.connectedUserStateService.hasRight(Rights.FollowFolder)
                    ? tabIndex + this.tabOffset + 1
                    : tabIndex + this.tabOffset;
        }
    }

    /**
     * Crée un onglet à un emplacement spécifique pour acceder au détail d'un dossier.
     * @param positionIndex : position de l'onglet
     * @param folderTabInfos Infos du dossier a afficher dans l'onglet.
     * @param selectAfterAdding Valeur indiquant si l'onglet doit être sélectionné après sa création.
     */
    private insertTab(positionIndex: number, folderTabInfos: FolderTabInfos, selectAfterAdding: boolean): void {
        let tabIndex = this.tabs.findIndex((x) => x.folderId === folderTabInfos.folderId);

        // Crée un nouvel onglet si le dossier n'est pas déjà ouvert dans un onglet existant.
        this.openedFoldersStateService.openedFolders.set(folderTabInfos.folderId, folderTabInfos);
        this.tabs.splice(positionIndex + this.tabOffset, 0, folderTabInfos);
        tabIndex = this.tabs.length - 1;

        // Sélectionne l'onglet, si cela est demandé.
        if (selectAfterAdding) {
            this.selectedTabIndex = positionIndex + this.tabOffset;
        }
    }

    /**
     * Supprime un onglet.
     * @param folderId Id du dossier pour lequel on veut fermer l'onglet.
     * @param : Valeur indiquant si le dossier a été retiré de la source de donnée persistente (CESAR STORAGE ou ElasticSearch)
     *
     */
    private removeTab(folderId: string, isFolderRemoved: boolean = false): void {
        if (this.openedFoldersStateService.openedFolders.hasKey(folderId)) {
            const folderInfos = this.openedFoldersStateService.openedFolders.get(folderId);
            this.openedFoldersStateService.openedFolders.remove(folderId);

            let tabIndex = this.tabs.findIndex((x) => x.folderId === folderId);
            this.tabs.splice(tabIndex, 1);

            // Libère le lock sur le dossier.
            if (folderInfos.readonly === false && isFolderRemoved === false) {
                // Dans le cas d'un dossier en edition et qui est toujours présent dans la source de donnée persistente --> Deverrouillage du dossier.
                this.foldersService.unlock(folderInfos.view.id, folderId).pipe(takeUntil(this.componentDestroyed$)).subscribe();
            }
        }
    }
}
